// Appointment reminders by SMS (docs/plans/phase2-appointments.md §4, §5).
//
// When a reminder goes, and what it says. The text carries the facility's name, the date
// and the time: never the patient's name, the clinic, the practitioner or the reason,
// because phones are shared and a clinic's name can itself be clinical information
// (HIV, mental health). The builder takes only those two inputs, so nothing else can
// reach the message.

import { eatDayOf, eatTimeOf, formatEatDate, formatEatTime, startOfEatDay } from '../time.ts';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Provisional policy, TODO(business): plan §12 B1. The evening before at 18:00; never in
 * the quiet hours 20:00–07:00; none at all within two hours of the start.
 */
export const REMINDER_POLICY = {
  eveningBeforeHour: 18,
  quietFromHour: 20,
  quietUntilHour: 7,
  minimumLeadMinutes: 120,
} as const;

/** One SMS in the GSM 7-bit alphabet. */
export const SMS_MAX_LENGTH = 160;
const FACILITY_NAME_MAX = 40;

/**
 * When to send the reminder for an appointment starting at `startsAt`, booked (or moved)
 * at `now`; null when it is too late for one to be useful.
 */
export function reminderDueAt(startsAt: Date, now: Date): Date | null {
  const dayBefore = startOfEatDay(eatDayOf(startsAt)).getTime() - 24 * HOUR_MS;
  const target = new Date(dayBefore + REMINDER_POLICY.eveningBeforeHour * HOUR_MS);
  let candidate = target.getTime() > now.getTime() ? target : now;
  const { hour } = eatTimeOf(candidate);
  if (hour >= REMINDER_POLICY.quietFromHour || hour < REMINDER_POLICY.quietUntilHour) {
    const midnight = startOfEatDay(eatDayOf(candidate)).getTime();
    const morning = midnight + REMINDER_POLICY.quietUntilHour * HOUR_MS;
    candidate = new Date(hour < REMINDER_POLICY.quietUntilHour ? morning : morning + 24 * HOUR_MS);
  }
  const lead = startsAt.getTime() - candidate.getTime();
  return lead >= REMINDER_POLICY.minimumLeadMinutes * MINUTE_MS ? candidate : null;
}

// The GSM 03.38 basic character set (no escape-table characters, which count double and
// some phones render badly).
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_SET = new Set(GSM7_BASIC);

export function isGsm7(text: string): boolean {
  for (const ch of text) if (!GSM7_SET.has(ch)) return false;
  return true;
}

const LOOKALIKES: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
};

/** A facility name reduced to the GSM alphabet and cut to fit one SMS. */
export function smsFacilityName(name: string): string {
  let out = '';
  for (const ch of name) {
    const mapped = LOOKALIKES[ch] ?? ch;
    if (GSM7_SET.has(mapped) && mapped !== '\n' && mapped !== '\r') out += mapped;
  }
  const tidy = out.replace(/\s+/g, ' ').trim().slice(0, FACILITY_NAME_MAX).trim();
  return tidy === '' ? 'your clinic' : tidy;
}

/**
 * The reminder, in English. TODO(business): plan §12 B7, a Kiswahili version once the
 * wording is approved.
 */
export function appointmentReminderText(input: {
  readonly facilityName: string;
  readonly startsAt: Date;
}): string {
  return `Reminder from ${smsFacilityName(input.facilityName)}: you have an appointment on ${formatEatDate(input.startsAt)} at ${formatEatTime(input.startsAt)}. If you cannot come, please call us.`;
}
